
import { namespace } from 'vuex-class';
import { Component, Emit, Mixins, Prop } from 'vue-property-decorator';

import BasicButton from '@/ui-components/BasicButton.vue';
import FlagSwitcher from '@/components/sports/common/FlagSwitcher.vue';
import ProviderIcon from '@/components/picture/ProviderIcon.vue';
import { AccountModal } from '@/lazy-load-components';
import OfferReporter from '@/components/buybox/OfferReporter.vue';

import { BuyboxParam } from '@/helpers/offer-helper';
import { ClickoutUrl } from '@/helpers/clickout-helper';
import { SponsoredClickoutsHelper } from '@/helpers/sponsored-clickouts-helper';
import { SnowplowContext } from '@/helpers/tracking/providers/snowplow-contexts';

import { ObjectType } from '@/@types/graphql-types';

import type Provider from '@/interfaces/provider';
import type { WebLocale } from '@/enums/web-locale';
import type { SportOffer } from '@/@types/graphql-types';
import { AppDirection } from '@/enums/app-direction';
import { TrackingHelper } from '@/helpers/tracking/tracking-helper';
import ToggleMySportsNotificationMutation from '@/mixins/ToggleMySportsNotificationMutation.vue';

import UseOfferImpressionTracking from '@/helpers/composables/useOfferImpressionTracking';

const Constant = namespace('constant');
const Language = namespace('language');
const Meta = namespace('meta');

@Component({
	name: 'EventBuybox',
	components: {
		ProviderIcon,
		FlagSwitcher,
		BasicButton,
		AccountModal,
		OfferReporter,
		UseOfferImpressionTracking,
	},
})
export default class EventBuybox extends Mixins(ToggleMySportsNotificationMutation) {
	// offers get created with a domainSessionId in their link out url.
	// this is needed since domainSessionId is not available beforehand, but still the link needs to be created with contexts.
	// changing domainSessionId later will also trigger currentOffers anew.
	protected domainSessionId = '' as string | undefined;

	@Prop({ default: () => [] }) offers: SportOffer[];
	@Prop({ required: true }) titleId: string;
	@Prop({ required: true }) titleName: string;
	@Prop({ required: true }) titleObjectId: number;
	@Prop({ required: true }) isNotificationActive: boolean;
	@Prop({ default: () => [] }) additionalContexts: SnowplowContext[];

	@Constant.Getter providersById: Record<string, Provider>;

	@Language.State webLocale: WebLocale;
	@Language.Getter countryEmoji: string;
	@Language.Getter country: string;

	@Meta.Getter appDirection: AppDirection;

	@Emit('countrySwitcherClick')
	handleCountrySwitcherClick() {}

	@Emit('clickOut')
	handleClickOut(offer: SportOffer, index: number) {
		return { offer, index };
	}

	get sport() {
		return this.$route.meta?.sport;
	}

	get isRTL(): boolean {
		return this.appDirection === AppDirection.RTL;
	}

	get titleObjectType() {
		return ObjectType.SportEvent;
	}

	getOfferClickoutUrl(offer: SportOffer) {
		return new ClickoutUrl(offer)
			.set('uct_country', this.country)
			.set('uct_buybox', BuyboxParam.Sport)
			.set('sid', this.domainSessionId)
			.set('uct_sport', this.sport)
			.toString();
	}

	isSponsoredOffer(offer: SportOffer) {
		return SponsoredClickoutsHelper.isSponsoredOfferGraphql(offer.package.packageId, this.webLocale);
	}

	translateMonetizationType(presentationType: string, offer?: SportOffer) {
		if (offer?.package?.packageId === 695 && this.country === 'FR') return this.$tc('WEBAPP_FREE_TRIAL');

		return {
			FLATRATE: this.$t('WEBAPP_FLAT'),
			FLATRATE_AND_BUY: this.$t('SPORTS_WEBAPP_PPV'),
			BUY: this.$t('SPORTS_WEBAPP_PPV'),
			RENT: this.$t('SPORTS_WEBAPP_PPV'),
			ADS: this.$t('WEBAPP_FREE'),
			FREE: this.$t('WEBAPP_FREE'),
		}[presentationType];
	}

	translateObjectType(objectType: string) {
		return {
			HIGHLIGHT: this.$t('SPORTS_WEBAPP_HIGHLIGHTS'),
			MAIN_EVENT: this.$t('SPORTS_WEBAPP_LIVE'),
			INTERVIEW: this.$t('SPORTS_WEBAPP_INTERVIEW'),
		}[objectType];
	}

	async notifyMe() {
		await this.toggleMySportsNotification({
			eventId: this.titleId,
			isNotificationActive: this.isNotificationActive,
		});

		TrackingHelper.trackEvent(
			'userinteraction',
			{ action: 'notify_me_clicked', label: 'no_offers' },
			this.additionalContexts
		);
	}
}
